import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tool } from '../types';
import { useToolsApi } from '../apis/tools-apis';
import { 
  Search, 
  Plus, 
  Eye, 
  Trash2, 
  Loader2, 
  X, 
  Filter,
  Grid3X3,
  List,
  Settings,
  Play,
  Clock,
  Tag,
  User,
  Globe,
  Lock,
  MoreVertical,
  TrendingUp,
  Activity,
  Zap,
  Code,
  Database,
  Cloud,
  Cpu,
  FileText,
  Mail,
  Calendar,
  Image,
  MessageSquare,
  BarChart3,
  Webhook,
  Terminal,
  Puzzle,
  ChevronDown,
  ChevronUp,
  Pencil
} from 'lucide-react';
import _ from 'lodash';

import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { useToast } from "../components/ui/Toast";
import { useAuth } from '../contexts/AuthContext';
import { Badge } from "../components/ui/Badge";

const ITEMS_PER_PAGE = 20;

type ViewMode = 'grid' | 'list';
type FilterType = 'all' | 'owned' | 'shared' | 'core';
type SortType = 'name' | 'created' | 'updated' | 'type';

// Helper functions to reduce complexity
const hasAnyKeyword = (text: string, keywords: string[]): boolean => {
  return keywords.some(keyword => text.includes(keyword));
};

const getSearchIcon = (name: string): typeof Search | null => {
  const searchKeywords = ['search', 'google', 'bing'];
  return hasAnyKeyword(name, searchKeywords) ? Search : null;
};

const getDatabaseIcon = (name: string): typeof Database | null => {
  const databaseKeywords = ['database', 'sql', 'db'];
  return hasAnyKeyword(name, databaseKeywords) ? Database : null;
};

const getWebhookIcon = (name: string): typeof Webhook | null => {
  const webhookKeywords = ['api', 'webhook', 'http'];
  return hasAnyKeyword(name, webhookKeywords) ? Webhook : null;
};

const getMailIcon = (name: string): typeof Mail | null => {
  const mailKeywords = ['email', 'mail', 'smtp'];
  return hasAnyKeyword(name, mailKeywords) ? Mail : null;
};

const getCalendarIcon = (name: string): typeof Calendar | null => {
  const calendarKeywords = ['calendar', 'schedule', 'date'];
  return hasAnyKeyword(name, calendarKeywords) ? Calendar : null;
};

const getImageIcon = (name: string): typeof Image | null => {
  const imageKeywords = ['image', 'photo', 'picture'];
  return hasAnyKeyword(name, imageKeywords) ? Image : null;
};

const getMessageIcon = (name: string): typeof MessageSquare | null => {
  const messageKeywords = ['chat', 'message', 'slack'];
  return hasAnyKeyword(name, messageKeywords) ? MessageSquare : null;
};

const getAnalyticsIcon = (name: string): typeof BarChart3 | null => {
  const analyticsKeywords = ['analytics', 'chart', 'graph'];
  return hasAnyKeyword(name, analyticsKeywords) ? BarChart3 : null;
};

const getFileIcon = (name: string): typeof FileText | null => {
  const fileKeywords = ['file', 'document', 'pdf'];
  return hasAnyKeyword(name, fileKeywords) ? FileText : null;
};

const getCloudIcon = (name: string): typeof Cloud | null => {
  const cloudKeywords = ['cloud', 'aws', 'azure'];
  return hasAnyKeyword(name, cloudKeywords) ? Cloud : null;
};

const getCodeIcon = (name: string): typeof Code | null => {
  const codeKeywords = ['code', 'git', 'github'];
  return hasAnyKeyword(name, codeKeywords) ? Code : null;
};

const getTerminalIcon = (name: string): typeof Terminal | null => {
  const terminalKeywords = ['terminal', 'shell', 'command'];
  return hasAnyKeyword(name, terminalKeywords) ? Terminal : null;
};

const getCpuIcon = (name: string): typeof Cpu | null => {
  const cpuKeywords = ['cpu', 'process', 'system'];
  return hasAnyKeyword(name, cpuKeywords) ? Cpu : null;
};

// Tool icon mapping based on tool name or type
const getToolIcon = (tool: Tool) => {
  const name = tool.toolName.toLowerCase();
  
  // Check each icon type in order of specificity
  const icon = getSearchIcon(name) ||
               getDatabaseIcon(name) ||
               getWebhookIcon(name) ||
               getMailIcon(name) ||
               getCalendarIcon(name) ||
               getImageIcon(name) ||
               getMessageIcon(name) ||
               getAnalyticsIcon(name) ||
               getFileIcon(name) ||
               getCloudIcon(name) ||
               getCodeIcon(name) ||
               getTerminalIcon(name) ||
               getCpuIcon(name);
  
  return icon || Puzzle; // Default to Puzzle if no match found
};

export const ToolsList: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user, isAdmin } = useAuth();
  const toolsApi = useToolsApi();
  const containerRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(false);

  // Data state
  const [tools, setTools] = useState<Tool[]>([]);
  const [displayedTools, setDisplayedTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [sortType, setSortType] = useState<SortType>('name');
  const [showFilters, setShowFilters] = useState(false);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toolToDelete, setToolToDelete] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set());

  useEffect(() => {
    const controller = new AbortController();

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const getAllTools = false;
        const fetchedTools = await toolsApi.getTools(getAllTools);

        if (isMounted.current) {
          setTools(fetchedTools);
          setTotalCount(fetchedTools.length);
          setDisplayedTools(fetchedTools.slice(0, ITEMS_PER_PAGE));
          setHasMore(fetchedTools.length > ITEMS_PER_PAGE);
        }
      } catch (error: unknown) {
        if (isMounted.current) {
          if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error fetching tools:', error);
            addToast({
              title: "Error",
              description: "Failed to retrieve tools. Please try again.",
              variant: "destructive",
            });
          }
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    isMounted.current = true;
    fetchInitialData();

    return () => {
      isMounted.current = false;
      controller.abort();
    };
  }, []);

  // Filter and sort tools
  const getFilteredAndSortedTools = useCallback(() => {
    let filtered = tools;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(tool =>
        tool.toolName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.toolDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.toolTags?.some(tag => tag && typeof tag === 'string' && tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply type filter
    switch (filterType) {
      case 'owned':
        filtered = filtered.filter(tool => tool.ownerId === user?.sub);
        break;
      case 'shared':
        filtered = filtered.filter(tool => tool.ownerId !== user?.sub);
        break;
      case 'core':
        filtered = filtered.filter(tool => tool.isCore);
        break;
      default:
        break;
    }

    // Apply sorting
    switch (sortType) {
      case 'name':
        filtered = _.sortBy(filtered, 'toolName');
        break;
      case 'created':
        filtered = _.sortBy(filtered, 'createdAt').reverse();
        break;
      case 'updated':
        filtered = _.sortBy(filtered, 'updatedAt').reverse();
        break;
      case 'type':
        filtered = _.sortBy(filtered, 'toolType');
        break;
      default:
        break;
    }

    return filtered;
  }, [tools, searchTerm, filterType, sortType, user?.sub]);

  const filteredTools = getFilteredAndSortedTools();

  // Statistics
  const stats = {
    total: tools.length,
    owned: tools.filter(tool => tool.ownerId === user?.sub).length,
    shared: tools.filter(tool => tool.ownerId !== user?.sub).length,
  };

  const debouncedSearch = _.debounce((term: string) => {
    setSearchTerm(term);
  }, 300);

  const handleViewTool = (toolId: string) => {
    navigate(`/studio/tools/view/${toolId}`);
  };

  const handleTestTool = (toolId: string) => {
    navigate(`/studio/tools/test/${toolId}`);
  };

  const handleDeleteClick = (id: string) => {
    setToolToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!toolToDelete) return;

    setIsDeletingId(toolToDelete);
    try {
      await toolsApi.deleteTool(toolToDelete);
      addToast({
        title: "Success",
        description: "Tool successfully deleted",
        variant: "default",
      });

      // Refresh tools list
      const getAllTools = false;
      const fetchedTools = await toolsApi.getTools(getAllTools);
      setTools(fetchedTools);
      setTotalCount(fetchedTools.length);
      setDisplayedTools(fetchedTools.slice(0, ITEMS_PER_PAGE));
      setHasMore(fetchedTools.length > ITEMS_PER_PAGE);
    } catch (error) {
      addToast({
        title: "Error",
        description: "Failed to delete tool. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingId(null);
      setDeleteDialogOpen(false);
      setToolToDelete(null);
    }
  };

  const toggleTags = (toolId: string) => {
    const newExpanded = new Set(expandedTags);
    if (newExpanded.has(toolId)) {
      newExpanded.delete(toolId);
    } else {
      newExpanded.add(toolId);
    }
    setExpandedTags(newExpanded);
  };

  const renderStatsCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <Card className="bg-gradient-to-br from-blue-500 to-blue-600 border-0 text-white shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Tools</p>
              <p className="text-3xl font-bold text-white">{stats.total}</p>
              <p className="text-blue-100 text-xs mt-1">Available in your workspace</p>
            </div>
            <div className="bg-white/20 p-3 rounded-full">
              <Zap className="h-6 w-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 border-0 text-white shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm font-medium">My Tools</p>
              <p className="text-3xl font-bold text-white">{stats.owned}</p>
              <p className="text-emerald-100 text-xs mt-1">Created and owned by you</p>
            </div>
            <div className="bg-white/20 p-3 rounded-full">
              <User className="h-6 w-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-violet-500 to-violet-600 border-0 text-white shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-violet-100 text-sm font-medium">Shared Tools</p>
              <p className="text-3xl font-bold text-white">{stats.shared}</p>
              <p className="text-violet-100 text-xs mt-1">Shared by other users</p>
            </div>
            <div className="bg-white/20 p-3 rounded-full">
              <Globe className="h-6 w-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderToolCard = (tool: Tool) => {
    const isOwned = tool.ownerId === user?.sub;
    const canEdit = isOwned || isAdmin;
    const canDelete = canEdit && !tool.isCore;
    const ToolIcon = getToolIcon(tool);
    const isTagsExpanded = expandedTags.has(tool.toolId || '');

    return (
      <Card key={tool.toolId} className="group hover:shadow-xl transition-all duration-300 border-emerald-200 hover:border-emerald-400 hover:scale-[1.02] relative ring-2 ring-transparent hover:ring-emerald-200/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            {/* Left side - Icon and Info */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* Tool Icon */}
              <div className="bg-gradient-to-br from-emerald-100 to-teal-200 p-3 rounded-xl flex-shrink-0 shadow-sm">
                <ToolIcon className="h-6 w-6 text-emerald-600" />
              </div>
              
              {/* Tool Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors truncate tracking-tight">
                    {tool.toolName}
                  </h3>
                  {isOwned && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-emerald-400 to-emerald-500 text-white shadow-sm flex-shrink-0">
                      <User className="w-3 h-3 mr-1" />
                      Owned
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                  {tool.toolDescription || 'No description provided.'}
                </p>
              </div>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleViewTool(tool.toolId || '')}
                                    className="h-8 w-8 p-0 hover:bg-emerald-50 hover:text-emerald-600"
                title="View Tool"
              >
                <Eye className="h-4 w-4" />
              </Button>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/studio/tools/edit/${tool.toolId}`)}
                  className="h-8 w-8 p-0 hover:bg-violet-50 hover:text-violet-600"
                  title="Edit Tool"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleTestTool(tool.toolId || '')}
                className="h-8 w-8 p-0 hover:bg-emerald-50 hover:text-emerald-600"
                title="Test Tool"
              >
                <Play className="h-4 w-4" />
              </Button>
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteClick(tool.toolId || '')}
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                  disabled={isDeletingId === tool.toolId}
                  title="Delete Tool"
                >
                  {isDeletingId === tool.toolId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Bottom section - Stats and Tags */}
          <div className="space-y-3">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1 font-medium">
                <Settings className="w-3 h-3 text-blue-500" />
                {tool.toolParameters?.parameters?.length || 0} params
              </span>
              <span className="flex items-center gap-1 font-medium">
                <Clock className="w-3 h-3 text-emerald-500" />
                {tool.toolTimeout || '30'}s
              </span>
              {tool.toolTags && tool.toolTags.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleTags(tool.toolId || '')}
                  className="flex items-center gap-1 font-medium text-violet-600 hover:text-violet-700 hover:bg-violet-50 h-auto p-1 -ml-1"
                >
                  <Tag className="w-3 h-3" />
                  {tool.toolTags.length} tags
                  {isTagsExpanded ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                </Button>
              )}
            </div>

            {/* Expandable Tags Section */}
            {tool.toolTags && tool.toolTags.length > 0 && isTagsExpanded && (
              <div className="pt-2 border-t border-gray-100">
                <div className="flex flex-wrap gap-1">
                  {tool.toolTags.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="text-xs bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderListItem = (tool: Tool) => {
    const isOwned = tool.ownerId === user?.sub;
    const canEdit = isOwned || isAdmin;
    const canDelete = canEdit && !tool.isCore;
    const ToolIcon = getToolIcon(tool);
    const isTagsExpanded = expandedTags.has(tool.toolId || '');

    return (
      <Card key={tool.toolId} className="mb-3 hover:shadow-lg transition-all duration-200 border-emerald-200 hover:border-emerald-400 group ring-1 ring-transparent hover:ring-emerald-200/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* Tool Icon */}
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-2 rounded-lg flex-shrink-0">
                <ToolIcon className="h-5 w-5 text-blue-600" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-gray-900 truncate tracking-tight">
                    {tool.toolName}
                  </h3>
                  <div className="flex items-center gap-2">
                    {isOwned && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-emerald-400 to-emerald-500 text-white shadow-sm">
                        <User className="w-3 h-3 mr-1" />
                        Owned
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-1 truncate">
                  {tool.toolDescription || 'No description provided.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewTool(tool.toolId || '')}
                className="border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300"
              >
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/studio/tools/edit/${tool.toolId}`)}
                  className="border-violet-200 text-violet-600 hover:bg-violet-50 hover:border-violet-300"
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTestTool(tool.toolId || '')}
                className="border-emerald-200 text-emerald-600 hover:bg-emerald-50 hover:border-emerald-300"
              >
                <Play className="h-4 w-4 mr-1" />
                Test
              </Button>
              {canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteClick(tool.toolId || '')}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300"
                  disabled={isDeletingId === tool.toolId}
                >
                  {isDeletingId === tool.toolId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Bottom section - Stats and Tags */}
          <div className="space-y-3">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1 font-medium">
                <Settings className="w-3 h-3 text-blue-500" />
                {tool.toolParameters?.parameters?.length || 0} parameters
              </span>
              <span className="flex items-center gap-1 font-medium">
                <Clock className="w-3 h-3 text-emerald-500" />
                {tool.toolTimeout || '30'}s timeout
              </span>
              {tool.toolTags && tool.toolTags.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleTags(tool.toolId || '')}
                  className="flex items-center gap-1 font-medium text-violet-600 hover:text-violet-700 hover:bg-violet-50 h-auto p-1 -ml-1"
                >
                  <Tag className="w-3 h-3" />
                  {tool.toolTags.length} tags
                  {isTagsExpanded ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                </Button>
              )}
            </div>

            {/* Expandable Tags Section */}
            {tool.toolTags && tool.toolTags.length > 0 && isTagsExpanded && (
              <div className="pt-2 border-t border-gray-100">
                <div className="flex flex-wrap gap-1">
                  {tool.toolTags.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="text-xs bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Spinner className="w-8 h-8 text-blue-500" />
        <p className="text-gray-600">Loading tools...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl font-['Poppins','SF_Pro_Display','system-ui','-apple-system','sans-serif'] antialiased" ref={containerRef}>
      {/* Hero Header Section */}
      <div className="relative mb-8 overflow-hidden">
        {/* Multi-layer gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-800"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/30 via-transparent to-teal-500/30"></div>
        
        {/* Animated background elements */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-400/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-emerald-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>
        
        {/* Glassmorphism overlay */}
        <div className="absolute inset-0 backdrop-blur-sm bg-gradient-to-r from-white/10 to-transparent"></div>
        
        {/* Content */}
        <div className="relative z-10 px-4 sm:px-8 py-8 sm:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-2xl shadow-lg self-start">
                  <Zap className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-2 drop-shadow-lg">
                    Integration Tools
                  </h1>
                  <p className="text-emerald-100 text-base sm:text-lg font-medium max-w-2xl leading-relaxed">
                    Connect external APIs and services to extend your agents' capabilities with powerful integrations and custom tools
                  </p>
                </div>
              </div>
              
              {/* Live Stats */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-6">
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-white">{stats.total}</span> Total Tools
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-emerald-200">{stats.owned}</span> My Tools
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-teal-200">{stats.shared}</span> Shared Tools
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action Button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/studio/tools/add')}
                className="group relative bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30 hover:border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-semibold text-base sm:text-lg w-full sm:w-auto"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-xl group-hover:bg-white/30 transition-colors">
                    <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <span>Create Tool</span>
                </div>
                
                {/* Subtle glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </Button>
            </div>
          </div>
        </div>
        

      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search tools"
              className="pl-10 pr-4 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                debouncedSearch(e.target.value);
              }}
            />
            {inputValue && (
              <button
                onClick={() => {
                  setInputValue('');
                  setSearchTerm('');
                  debouncedSearch('');
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as FilterType)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="all">All Tools</option>
            <option value="owned">My Tools</option>
            <option value="shared">Shared Tools</option>
            <option value="core">Core Tools</option>
          </select>

          <select
            value={sortType}
            onChange={(e) => setSortType(e.target.value as SortType)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="name">Sort by Name</option>
            <option value="created">Sort by Created</option>
            <option value="updated">Sort by Updated</option>
            <option value="type">Sort by Type</option>
          </select>

          <div className="flex items-center border border-gray-300 rounded-lg bg-white">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-blue-500'}`}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-blue-500'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-600 font-medium">
          {searchTerm || filterType !== 'all' ? (
            <>Showing {filteredTools.length} of {stats.total} tools</>
          ) : (
            <>Showing all {stats.total} tools</>
          )}
        </p>
      </div>

      {/* Tools Grid/List */}
      {filteredTools.length === 0 ? (
        <Card className="p-12 text-center border-2 border-dashed border-gray-300">
          <div className="flex flex-col items-center gap-4">
            <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-4 rounded-full">
              <Zap className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 tracking-tight font-['Radley','serif']">
                {searchTerm ? 'No tools found' : 'No tools yet'}
              </h3>
              <p className="text-gray-600 mb-4 max-w-md">
                {searchTerm 
                  ? 'Try adjusting your search or filters to find what you\'re looking for.'
                  : 'Get started by creating your first tool to extend your agents\' capabilities with external APIs and services.'
                }
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => navigate('/studio/tools/add')}
                  className="group relative bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-8 py-4 rounded-xl font-semibold text-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-lg group-hover:bg-white/30 transition-colors">
                      <Plus className="h-5 w-5" />
                    </div>
                    <span>Create Your First Tool</span>
                  </div>
                  
                  {/* Subtle glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-500 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 -z-10 blur-xl"></div>
                </Button>
              )}
            </div>
          </div>
        </Card>
      ) : (
        <div className={
          viewMode === 'grid' 
            ? 'grid gap-4 grid-cols-1 lg:grid-cols-2' 
            : 'space-y-3'
        }>
          {filteredTools.map(tool => 
            viewMode === 'grid' ? renderToolCard(tool) : renderListItem(tool)
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title="Delete Tool"
        description="Are you sure you want to permanently delete this tool? This action cannot be undone."
        confirmLabel={isDeletingId ? "Deleting..." : "Yes, Delete"}
        cancelLabel="Cancel"
        disabled={Boolean(isDeletingId)}
        requireVerification={false}
      />
    </div>
  );
};

export default ToolsList;